import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, MessageSquare, Handshake, MapPin, ShieldCheck, Sparkles, PlusCircle } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/data";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HeroSearch from "../components/HeroSearch";
import CategoryIcon from "../components/CategoryIcon";
import ProjectsCarousel from "../components/ProjectsCarousel";
import MasterPlanCarousel from "../components/MasterPlanCarousel";
import RecentListingsSlider from "../components/RecentListingsSlider";
import RequirementsForm from "../components/RequirementsForm";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";

const STEPS = [
  { icon: FileText, title: "Post Listing", desc: "Create your free listing in three simple steps." },
  { icon: MessageSquare, title: "Get Contacted", desc: "Interested buyers reach out to you directly." },
  { icon: Handshake, title: "Close the Deal", desc: "Connect, negotiate and seal the deal. Done." },
];

export default function Home() {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get("/listings/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="India's Smart Marketplace to Buy, Sell & Grow | The Pataudi" description="The Pataudi is a powerful online marketplace where you can buy, sell, and list anything easily. Connect with real buyers instantly and grow your business faster." />
      <Header />

      <main className="flex-1">
        {/* Hero — clean, minimal, focus on search */}
        <section className="relative hero-gradient" data-testid="hero-section">
          <div className="absolute inset-0 dot-grid opacity-70 pointer-events-none" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-20 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur ring-1 ring-primary/20 px-3 py-1 text-[11px] uppercase tracking-[0.16em] font-semibold text-ink">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Trusted local marketplace
            </span>
            <h1 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.02] text-ink">
              Find your <span className="text-gradient-green">perfect property</span>,<br className="hidden sm:block" /> vehicle &amp; more.
            </h1>
            <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">A modern marketplace for Gurgaon, Pataudi &amp; nearby — verified listings, direct owner contact, zero clutter.</p>

            <div className="mt-6 max-w-3xl mx-auto">
              <HeroSearch />
            </div>

            {/* Post Free Listing CTA — visible on hero on desktop */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Button asChild className="bg-primary hover:bg-primary/90 text-white rounded-full h-11 px-5 gap-1.5 shadow-lift whitespace-nowrap">
                <Link to="/dashboard/create-listing" data-testid="hero-post-free-btn"><PlusCircle className="h-4 w-4 shrink-0" /> Post Free Listing</Link>
              </Button>
              <Button
                asChild
                className="rounded-full h-11 px-5 gap-1.5 whitespace-nowrap text-white font-semibold shadow-[0_6px_18px_-6px_rgba(184,134,11,0.55)] hover:shadow-[0_10px_26px_-6px_rgba(184,134,11,0.65)] border border-[#D4AF37] transition-shadow"
                style={{ background: "linear-gradient(135deg,#B8860B 0%,#14532D 100%)" }}
              >
                <Link to="/requirements" data-testid="hero-req-btn"><MessageSquare className="h-4 w-4 shrink-0" /> Post Requirement</Link>
              </Button>
            </div>
            {/* Browse All Requirements — third-row CTA (centered, subtle) */}
            <div className="mt-3 flex justify-center">
              <Button asChild variant="outline" className="rounded-full h-10 px-5 gap-1.5 whitespace-nowrap border-primary/30 text-primary hover:bg-primary/5">
                <Link to="/requirements" data-testid="hero-browse-req-btn">Browse All Requirements <ArrowRight className="h-4 w-4 shrink-0" /></Link>
              </Button>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-ink/80">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-ink" /> Verified sellers</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-ink" /> 8 local cities</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-ink" /> Zero broker spam</span>
            </div>
          </div>
        </section>

        {/* Master Plan — city / sector master plans (below hero, above Curated Projects) */}
        <MasterPlanCarousel />

        {/* Projects — auto-scrolling carousel (top section) */}
        <ProjectsCarousel />

        {/* Categories — compact icons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="category-grid">
          <div className="flex items-end justify-between gap-4 mb-2">
            <div>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-ink">Browse by Category</h2>
              <p className="text-muted-foreground text-sm mt-1">Pick a category to explore listings near you.</p>
            </div>
            <Link to="/listings" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">See all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2.5">
            {CATEGORIES.map((c) => (
              <Link
                key={c.id}
                to={`/listings/${c.id}`}
                className="group relative bg-card rounded-xl shadow-soft border border-border p-2.5 hover:shadow-lift hover:-translate-y-0.5 hover:border-primary/20 transition-all duration-200 flex flex-col items-center text-center"
                data-testid={`category-${c.id}`}
              >
                <div className="h-9 w-9 rounded-lg bg-ink/5 flex items-center justify-center text-ink group-hover:bg-primary group-hover:text-white transition-colors">
                  <CategoryIcon name={c.icon} className="h-4 w-4" />
                </div>
                <h3 className="mt-1.5 font-semibold text-ink text-[11px] leading-tight line-clamp-2">{c.label}</h3>
                <p className="text-[10px] text-muted-foreground mt-0.5">{counts[c.id] || 0}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Recent listings — horizontal scroll per category (matches reference) */}
        <RecentListingsSlider />

        {/* How it works */}
        <section className="bg-white border-y border-border" data-testid="how-it-works">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-ink text-center">How It Works</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {STEPS.map((s, i) => (
                <div key={i} className="text-center" data-testid={`step-${i + 1}`}>
                  <div className="mx-auto h-12 w-12 rounded-full bg-ink/5 flex items-center justify-center text-ink">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 font-display font-semibold text-ink">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Post Your Requirement */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="home-requirements">
          <div className="bg-gradient-to-br from-primary/5 via-white to-accent/5 border border-primary/10 rounded-3xl p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wider">
                  <MessageSquare className="h-3 w-3" /> Post Your Requirement
                </span>
                <h2 className="mt-2 font-display font-bold text-2xl md:text-3xl text-ink">Can't find what you're looking for?</h2>
                <p className="text-muted-foreground text-sm mt-2 max-w-md">Tell us your need — from a 3BHK in Gurgaon to a wedding photographer — and matching sellers will reach out to you directly. It's free.</p>
                <Link to="/requirements" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline" data-testid="home-view-requirements">
                  Browse all requirements <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="bg-white rounded-2xl border border-border p-4 md:p-5 shadow-soft">
                <RequirementsForm variant="compact" />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
